import { CLEAR_ALL, LOGOUT, SELECT_PAYMENT_METHOD } from '@comeon/actions-public';
import { insertTrackEvent } from '@comeon/analytics';
import reducerRegistry from '@comeon/reducer-registry';
import { selectCurrency, selectRemainingGermanyBonusValue } from '~/components/account/ducks/index';
import { selectIsGermanRegulatedFranchise } from '~/components/brand/ducks/selectors';
import getFormattedBonus from '~/components/payment/deposit/data/formatBonusValue';
import { BONUS_PACKAGE, CAMPAIGN_ACCESS, DEPOSIT_BONUS, FREEBET, FREESPIN, OPT_IN } from '~/constants/index';
import { requestApi } from '../../../services/api-new';
import { ImmutableObject } from '../../../utils/immutable-type';
import { showQueuedNotification } from '../../notifications/ducks/actions';
import imageFormatHandler from '../../utilities/image-format';
import { Item, Response, ThunkAction } from '../types';
import * as CONSTANTS from './constants';
import { allowsMethod, isAllowedForMethod, sortById, wrappedRequestApi, type ResponseItem } from './public';

export const REDUCER_NAME = 'bonuses';
const SELECTED_DEPOSIT_FREESPIN_LOCAL_STORAGE = 'selectedDepositFreespin';
const SELECTED_DEPOSIT_FREEBET_LOCAL_STORAGE = 'selectedDepositFreebet';
const DEPOSIT_FREEBET_REDIRECT_URL = 'depositFreebetRedirectURL';
const SELECTE_BONUS_PACKAGE_LOCAL_STORAGE = 'selectedBonusPackageId';
const SELECTED_DEPOSIT_OPT_IN_LOCAL_STORAGE = 'selectedDepositOptIn';

function clearDepositFreebetRedirectURL() {
	if (!window.sessionStorage) {
		return;
	}
	window.sessionStorage.removeItem(DEPOSIT_FREEBET_REDIRECT_URL);
}

function clearDepositFreespinInSessionStorage() {
	if (!window.sessionStorage) {
		return;
	}
	window.sessionStorage.removeItem(SELECTED_DEPOSIT_FREESPIN_LOCAL_STORAGE);
}

function clearDepositFreebetInSessionStorage() {
	if (!window.sessionStorage) {
		return;
	}

	window.sessionStorage.removeItem(SELECTED_DEPOSIT_FREEBET_LOCAL_STORAGE);
}

function clearDepositOptInInSessionStorage() {
	if (!window.sessionStorage) {
		return;
	}

	window.sessionStorage.removeItem(SELECTED_DEPOSIT_OPT_IN_LOCAL_STORAGE);
}

function clearBonusPackageIdInSessionStorage() {
	if (!window.sessionStorage) {
		return;
	}
	window.sessionStorage.removeItem(SELECTE_BONUS_PACKAGE_LOCAL_STORAGE);
}

function formatBonusAmount(amount?: number, currency?: string) {
	return amount ? `${amount.toString()} ${currency || ''}` : null;
}

function clearBonusAll(): ThunkAction {
	return dispatch => {
		dispatch({ type: CONSTANTS.BONUS_CLEAR_ALL });
	};
}

function clearDepositFreespin(): ThunkAction {
	clearDepositFreespinInSessionStorage();
	return dispatch => {
		dispatch({ type: CONSTANTS.CLEAR_DEPOSIT_FREESPIN });
	};
}

function clearDepositFreebet(): ThunkAction {
	clearDepositFreebetInSessionStorage();
	clearBonusPackageIdInSessionStorage();
	clearDepositFreebetRedirectURL();
	return dispatch => {
		dispatch({ type: CONSTANTS.CLEAR_DEPOSIT_FREEBET });
	};
}

function clearDepositOptIn(): ThunkAction {
	clearDepositOptInInSessionStorage();
	return dispatch => {
		dispatch({ type: CONSTANTS.CLEAR_DEPOSIT_OPT_IN });
	};
}

const logPlayerRewardAction =
	(rewardId: number, rewardType: string, actionType: string): ThunkAction =>
	async (_dispatch, getState) => {
		const { id } = getState().account as { id: number };
		try {
			await requestApi({
				endpoint: `/rest/bonus/logPlayerRewardAction${id ? `?id=${id}` : ''}`,
				payload: JSON.stringify({
					rewardId,
					rewardType,
					actionType
				}),
				method: 'POST'
			});
		} catch (e) {
			/** Do nothing */
		}
	};

const isDepositBonusOk = (): ThunkAction => async dispatch => {
	const response: Response = await requestApi({ endpoint: '/rest/bonus/isDepositBonusOK' });
	const verificationErrorCodes = [
		'BONUS_ADD_NEEDS_VERIFIED_EMAIL_MOBILE',
		'BONUS_ADD_NEEDS_VERIFIED_EMAIL',
		'BONUS_ADD_NEEDS_VERIFIED_MOBILE'
	];

	if (response.status !== 'SUCCESS' && verificationErrorCodes.includes(response.errorCode)) {
		dispatch({
			type: CONSTANTS.CHECK_BONUS_FAILURE,
			payload: response
		});
	}
};

const closeVerification = (): ThunkAction => dispatch => {
	dispatch({
		type: CONSTANTS.FETCH_FREEMONEY_BONUS,
		payload: {
			verifyMobile: false
		}
	});
};

const redeemFreeMoneyBonusOverCarousel =
	(bonusId: number, successMessage: string, withinSidebar: boolean): ThunkAction =>
	async dispatch => {
		try {
			const params = { value: bonusId };
			const response = await wrappedRequestApi({
				endpoint: '/rest/bonus/redeemfreemoneybonus',
				payload: params,
				method: 'POST'
			});
			if (response && 'error' in response) {
				const {
					error: { status, errorDescription }
				} = response;
				dispatch(
					showQueuedNotification({
						message: errorDescription,
						color: 'red',
						autoHide: true,
						openInSidebar: withinSidebar
					})
				);
				if (status === 'FAILURE') {
					dispatch(
						showQueuedNotification({
							message: errorDescription,
							color: 'red',
							autoHide: true,
							openInSidebar: withinSidebar
						})
					);
					return dispatch({
						type: CONSTANTS.REDEEM_FMB_OVER_CAROUSEL_FAIL
					});
				}
			}
			dispatch(
				showQueuedNotification({
					message: successMessage,
					color: 'green',
					autoHide: true,
					openInSidebar: withinSidebar
				})
			);
			return dispatch({
				type: CONSTANTS.REDEEM_FMB_OVER_CAROUSEL_SUCCESS
			});
		} catch {
			return dispatch({
				type: CONSTANTS.REDEEM_FMB_OVER_CAROUSEL_FAIL
			});
		}
	};

const confirmFreeMoneyBonusResult = (): ThunkAction => dispatch => {
	dispatch({
		type: CONSTANTS.FETCH_FREEMONEY_BONUS,
		payload: { result: null }
	});
};

const clearBonus = (): ThunkAction => dispatch => {
	clearDepositFreespinInSessionStorage();
	clearDepositFreebetInSessionStorage();
	clearDepositOptInInSessionStorage();
	dispatch({
		type: CONSTANTS.CLEAR_BONUS
	});
};

const skipBonus =
	(location: string): ThunkAction =>
	dispatch => {
		if (location) {
			insertTrackEvent({
				category: 'Sidebar',
				action: 'Click - Bonus Carousel',
				label: `Skip - ${location}`
			});
		}
		dispatch({
			type: CONSTANTS.SKIP_BONUS
		});
	};

const clearActiveBonusDetails = (): ThunkAction => dispatch => {
	dispatch({ type: CONSTANTS.CLEAR_ACTIVE_BONUS_DETAILS });
};

const clearAvailableBonuses = (): ThunkAction => dispatch => {
	dispatch({ type: CONSTANTS.CLEAR_AVAILABLE_BONUSES });
};

const cancelPendingNavigation = (): ThunkAction => dispatch => {
	dispatch({
		type: CONSTANTS.CANCEL_PENDING_NAV
	});
};

const clearHasBonusError = (): ThunkAction => dispatch => {
	dispatch({
		type: CONSTANTS.CLEAR_HAS_BONUS_ERROR
	});
};

const toggleBonusDetailModal =
	(bool: boolean): ThunkAction =>
	dispatch => {
		dispatch({
			type: CONSTANTS.TOGGLE_SHOW_BONUS_MODAL,
			payload: bool
		});
	};

const toggleBonusLoadingState =
	(bool: boolean): ThunkAction =>
	dispatch => {
		dispatch({
			type: CONSTANTS.TOGGLE_BONUS_LOADING_STATE,
			payload: { bool }
		});
	};

const fetchFmbOverCarousel =
	(bonusId: number): ThunkAction =>
	async dispatch => {
		dispatch({ type: CONSTANTS.FETCH_FMB_START });
		const URL = `/rest/bonus/${bonusId}`;
		try {
			const response = await wrappedRequestApi({ endpoint: URL });
			if (response && !('error' in response)) {
				const result = response;
				if ('id' in result) {
					const fmb = {
						amount: result.bonusAmount,
						header: result.header,
						bonusId: result.id,
						bonusType: result.textBonusType,
						validProducts: result.validProducts,
						daysToPlay: result.daysToPlay,
						imageView: result.imageView
					};

					return dispatch({
						type: CONSTANTS.FETCH_FMB_SUCCESS,
						payload: fmb
					});
				}
			} else {
				return dispatch({ type: CONSTANTS.FETCH_FMB_FAILURE });
			}
			dispatch({ type: CONSTANTS.FETCH_FMB_FAILURE });
			throw new Error('Failed to fetch deposit offer');
		} catch (e) {
			dispatch({ type: CONSTANTS.FETCH_FMB_FAILURE });
			throw new Error('Failed to fetch deposit offer');
		}
	};

const fetchPreSelectedBonus =
	(bonusId: number): ThunkAction =>
	async dispatch => {
		dispatch({ type: CONSTANTS.FETCH_PRESELECTED_BONUS_LOADING });

		const URL = `/rest/bonus/preselectedbonus/${bonusId}`;
		try {
			const response = await wrappedRequestApi({ endpoint: URL });
			if (response && !('error' in response)) {
				return dispatch({
					type: CONSTANTS.FETCH_PRESELECTED_BONUS_SUCCESS,
					payload: response
					// payload: mockPreSelectedBonus
				});
			}
			dispatch({ type: CONSTANTS.FETCH_PRESELECTED_BONUS_FAILURE });
			throw new Error('Failed to fetch preselected bonus');
		} catch (e) {
			dispatch({ type: CONSTANTS.FETCH_PRESELECTED_BONUS_FAILURE });
			throw new Error('Failed to fetch preselected bonus');
		}
	};

function getBonusDepositItemFetchURL(type: string, bonusId: number, id: number) {
	let URL = '';
	switch (type) {
		case OPT_IN:
			URL = `/rest/deposit-opt-in/${bonusId}`;
			break;
		case CAMPAIGN_ACCESS:
			URL = `/rest/shop/items/campaign-access/${bonusId}`;
			break;
		case FREEBET:
			URL = `/freebet/${bonusId}`;
			break;
		case FREESPIN:
			URL = id ? `/freespin/depositfreespin/${bonusId}/${id}` : `/freespin/depositfreespin/${bonusId}`;
			break;
		default:
			URL = id ? `/rest/bonus/${bonusId}/${id}` : `/rest/bonus/${bonusId}`;
	}
	return URL;
}

function getBonusDepositItemFetchPayload(
	isDepositBonus: boolean,
	getState: () => ImmutableObject<{
		[sliceName: string]: unknown;
	}>,
	result: ResponseItem[] | ResponseItem,
	props: object
) {
	const remainingBonusValue = selectRemainingGermanyBonusValue(getState());
	const userCurrency = selectCurrency(getState());
	const germanRegulatedFranchise = selectIsGermanRegulatedFranchise(getState());
	const formattedBonus = getFormattedBonus(
		germanRegulatedFranchise,
		remainingBonusValue,
		result,
		userCurrency
	) as object;
	const data = isDepositBonus ? formattedBonus : result;
	return { data, props };
}

const fetchBonusDepositItem =
	(bonusId: number, type: string, props = {}): ThunkAction =>
	async (dispatch, getState) => {
		dispatch({ type: CONSTANTS.FETCH_DEPOSIT_OFFER_LOADING });
		const {
			account: { id }
		} = getState() as { account: { id: number } };
		const URL = getBonusDepositItemFetchURL(type, bonusId, id);
		try {
			const response = await wrappedRequestApi({ endpoint: URL });
			if (response && !('error' in response)) {
				const payload = getBonusDepositItemFetchPayload(type === DEPOSIT_BONUS, getState, response, props);
				return dispatch({
					type: CONSTANTS.FETCH_DEPOSIT_OFFER_SUCCESS,
					payload
				});
			}
			throw new Error('Failed to fetch deposit offer');
		} catch (e) {
			// eslint-disable-next-line no-console
			console.warn(e);
			return dispatch({ type: CONSTANTS.FETCH_DEPOSIT_OFFER_FAILURE });
		}
	};

const getWelcomeBonusPackages =
	(encryptedPlayerId: string | number, noaccount = false): ThunkAction =>
	async (dispatch, _getState, { isPictureElementEnabled, imageDescriptors, imageFormats }) => {
		try {
			const response = await wrappedRequestApi({
				endpoint: `/rest/welcomebonuspackages${
					encryptedPlayerId ? `/${encryptedPlayerId}` : noaccount ? '/noaccount' : ''
				}`
			});

			if (response && !('error' in response)) {
				const result = 'length' in response ? response : [response];

				const items = isPictureElementEnabled
					? imageFormatHandler({ imageDescriptors, imageFormats })(result)
					: result;
				return dispatch({
					type: CONSTANTS.FETCH_WELCOME_BONUS_PACKAGE_SUCCESS,
					payload: items
				});
			}
			return dispatch({
				type: CONSTANTS.FETCH_BONUS_FAILURE
			});
		} catch (err) {
			return dispatch({
				type: CONSTANTS.FETCH_BONUS_FAILURE
			});
		}
	};

const getDepositOffers =
	(withId = false): ThunkAction =>
	async (dispatch, getState, { isPictureElementEnabled, imageDescriptors, imageFormats }) => {
		const {
			bonuses: { bonusesHydrated = false },
			account: { id },
			brand: { enableBonusPackage }
		} = getState() as {
			bonuses: { bonusesHydrated?: boolean };
			account: { id: number };
			brand: { enableBonusPackage: boolean };
		};

		if (bonusesHydrated) {
			return dispatch({ type: CONSTANTS.USING_HYDRATED_BONUSES });
		}

		dispatch({ type: CONSTANTS.CLEAR_AVAILABLE_BONUSES });
		dispatch({ type: CONSTANTS.FETCH_BONUS_START });
		if (enableBonusPackage) {
			return dispatch(getWelcomeBonusPackages(withId ? id : ''));
		}

		try {
			const timestamp = Date.now();
			const endpoint = withId ? `/rest/bonus/depositoffers/${id}` : `/rest/bonus/depositoffers?d=${timestamp}`;
			const response = await wrappedRequestApi({ endpoint });
			if (response && !('error' in response)) {
				const result = 'length' in response ? response : [response];

				const items = isPictureElementEnabled
					? imageFormatHandler({ imageDescriptors, imageFormats })(result)
					: result;
				const onId = sortById(items);
				const fmb = items.find(bonus => 'type' in bonus && bonus.type === 'FreeMoneyBonus');
				const depositOffersWithOutFmb = items.filter(
					bonus => 'type' in bonus && bonus.type !== 'FreeMoneyBonus'
				);
				if (fmb && 'id' in fmb) {
					dispatch(fetchFmbOverCarousel(fmb.id));
				}
				return dispatch({
					type: CONSTANTS.FETCH_BONUS_SUCCESS,
					payload: {
						result: depositOffersWithOutFmb,
						onId
					}
				});
			}
			return dispatch({
				type: CONSTANTS.FETCH_BONUS_FAILURE
			});
		} catch (e) {
			return dispatch({
				type: CONSTANTS.FETCH_BONUS_FAILURE
			});
		}
	};

const getNoAccountWelcomeBonuses =
	(accHistRef = '', errorNotificationMessage?: string): ThunkAction =>
	async (dispatch, getState, { isPictureElementEnabled, imageDescriptors, imageFormats }) => {
		const {
			brand: { enableBonusPackage }
		} = getState() as {
			brand: { enableBonusPackage: boolean };
		};

		dispatch({ type: CONSTANTS.FETCH_BONUS_START });

		const timestamp = Date.now();
		if (enableBonusPackage) {
			return dispatch(getWelcomeBonusPackages('', true));
		}

		try {
			const response = await wrappedRequestApi({
				endpoint: `/rest/bonus/depositoffers/noaccount?ref=${accHistRef}&d=${timestamp}`
			});

			if (response && !('error' in response)) {
				const result = 'length' in response ? response : [response];
				const items = isPictureElementEnabled
					? imageFormatHandler({ imageDescriptors, imageFormats })(result)
					: result;
				const onId = sortById(items);

				return dispatch({
					type: CONSTANTS.FETCH_BONUS_SUCCESS,
					payload: { result: items, onId }
				});
			}

			dispatch(
				showQueuedNotification({
					message: errorNotificationMessage,
					color: 'red',
					autoHide: true,
					openInSidebar: true
				})
			);

			return dispatch({
				type: CONSTANTS.FETCH_BONUS_FAILURE
			});
		} catch (err) {
			dispatch(
				showQueuedNotification({
					message: errorNotificationMessage,
					color: 'red',
					autoHide: true,
					openInSidebar: true
				})
			);
			return dispatch({
				type: CONSTANTS.FETCH_BONUS_FAILURE
			});
		}
	};

const setBonusPackageToActiveBonusDetail =
	(bonusPackage: Item): ThunkAction =>
	dispatch => {
		dispatch({
			type: 'SET_PACKAGE_AS_ACTIVE_BONUS_DETAILS',
			payload: {
				...bonusPackage,
				bonusType: BONUS_PACKAGE
			}
		});
	};

export const fetchExcludedGamesForId =
	(bonusId: number): ThunkAction =>
	async (dispatch, getState) => {
		dispatch({ type: CONSTANTS.FETCH_EXCLUDED_GAMES_FOR_ID_REQUESTED });
		const {
			account: { id }
		} = getState() as { account: { id: number } };
		const URL = id ? `/rest/bonus/${bonusId}/excludedgames/${id}` : `/rest/bonus/${bonusId}/excludedgames`;
		try {
			const response = await wrappedRequestApi({ endpoint: URL });
			if (response && !('error' in response)) {
				dispatch({
					type: CONSTANTS.FETCH_EXCLUDED_GAMES_FOR_ID_SUCCESS,
					payload: response
				});
			}
			dispatch({ type: CONSTANTS.FETCH_EXCLUDED_GAMES_FOR_ID_FAUILURE });
		} catch (error) {
			dispatch({ type: CONSTANTS.FETCH_EXCLUDED_GAMES_FOR_ID_FAUILURE });
			throw new Error(error as string);
		}
	};

export const fetchIncludedGamesForId =
	(bonusId: number): ThunkAction =>
	async (dispatch, getState) => {
		dispatch({ type: CONSTANTS.FETCH_INCLUDED_GAMES_FOR_ID_REQUESTED });
		const {
			account: { id }
		} = getState() as { account: { id: number } };
		const URL = id ? `/rest/bonus/${bonusId}/includedgames/${id}` : `/rest/bonus/${bonusId}/includedgames`;
		try {
			const response = await wrappedRequestApi({ endpoint: URL });
			if (response && !('response' in response)) {
				dispatch({
					type: CONSTANTS.FETCH_INCLUDED_GAMES_FOR_ID_SUCCESS,
					payload: response
				});
			}
			dispatch({ type: CONSTANTS.FETCH_INCLUDED_GAMES_FOR_ID_FAILURE });
		} catch (error) {
			dispatch({ type: CONSTANTS.FETCH_INCLUDED_GAMES_FOR_ID_FAILURE });
			throw new Error(error as string);
		}
	};

export const showOneClickOptInMessage = () => ({
	type: CONSTANTS.SHOW_ONE_CLICK_OPT_IN_MESSAGE
});

export const closeOneClickOptInMessage = () => ({
	type: CONSTANTS.HIDE_ONE_CLICK_OPT_IN_MESSAGE
});

export const showBonusMaxStakeMessage = (payload: object) => {
	return {
		type: CONSTANTS.SHOW_BONUS_MAX_STAKE_MESSAGE,
		payload
	};
};

export const closeBonusMaxStakeMessage = () => ({
	type: CONSTANTS.HIDE_BONUS_MAX_STAKE_MESSAGE
});

export const clearIncludedExcludedGamesForId = (): ThunkAction => dispatch => {
	dispatch({ type: CONSTANTS.CLEAR_INCLUDED_EXCLUDED_GAMES_FOR_ID });
};

export const setBonusSplitCategory =
	(category: string): ThunkAction =>
	(dispatch, getState) => {
		const {
			bonuses: { availableDepositOffers, unsplitDepositOffers }
		} = getState() as { bonuses: { availableDepositOffers: Item[]; unsplitDepositOffers: Item[] } };
		const categoryIsSportbook = category === CONSTANTS.BONUS_SPLIT_TYPE_SPORTS;
		const preSplitOffers = unsplitDepositOffers.length > 0 ? unsplitDepositOffers : availableDepositOffers;
		const splitOffers = preSplitOffers.filter(offer => offer.sportbookBonus === categoryIsSportbook);
		dispatch({
			type: CONSTANTS.SET_BONUS_SPLIT_CATEGORY,
			payload: {
				offers: splitOffers,
				unsplitOffers: availableDepositOffers,
				category
			}
		});
	};

const getDepositFreespinInSessionStorage = () => {
	if (!window.sessionStorage) {
		return null;
	}

	return window.sessionStorage.getItem(SELECTED_DEPOSIT_FREESPIN_LOCAL_STORAGE);
};

const getDepositFreebetInSessionStorage = () => {
	if (!window.sessionStorage) {
		return null;
	}

	return window.sessionStorage.getItem(SELECTED_DEPOSIT_FREEBET_LOCAL_STORAGE);
};

const getDepositOptInInSessionStorage = () => {
	if (!window.sessionStorage) {
		return null;
	}

	return window.sessionStorage.getItem(SELECTED_DEPOSIT_OPT_IN_LOCAL_STORAGE);
};

const getDepositFreebetRedirectUrlInSessionStorage = () => {
	if (!window.sessionStorage) {
		return null;
	}

	return window.sessionStorage.getItem(DEPOSIT_FREEBET_REDIRECT_URL);
};

const getBonusPackageIdInSessionStorage = () => {
	if (!window.sessionStorage) {
		return null;
	}
	return window.sessionStorage.getItem(SELECTE_BONUS_PACKAGE_LOCAL_STORAGE);
};

const redeemDepositOptIn =
	(optInId: number): ThunkAction =>
	async dispatch => {
		dispatch({ type: CONSTANTS.REDEEM_DEPOSIT_OPT_IN_START });
		// Is this the wrong end point????
		const URL = `/rest/deposit-opt-in/redeem/${optInId}`;
		try {
			const response = await wrappedRequestApi({ endpoint: URL, method: 'POST', urlencodedContentType: true });
			clearDepositOptInInSessionStorage();
			if (response && !('error' in response)) {
				dispatch({ type: CONSTANTS.REDEEM_DEPOSIT_OPT_IN_SUCCESS, payload: response });
				return response;
			}
			dispatch({ type: CONSTANTS.REDEEM_DEPOSIT_OPT_IN_FAILURE });
			return null;
		} catch (error) {
			dispatch({ type: CONSTANTS.REDEEM_DEPOSIT_OPT_IN_FAILURE });
			return null;
		}
	};

const redeemDepositFreebet =
	(freebetId: number): ThunkAction =>
	async dispatch => {
		dispatch({ type: CONSTANTS.REDEEM_DEPOSIT_FREEBET_START });
		const URL = `/freebet/redeem-deposit-freebet/${freebetId}`;
		try {
			const response = await wrappedRequestApi({ endpoint: URL, method: 'POST', urlencodedContentType: true });
			clearDepositFreebetInSessionStorage();
			if (response && !('error' in response)) {
				dispatch({
					type: CONSTANTS.REDEEM_DEPOSIT_FREEBET_SUCCESS,
					payload: response
				});
				return response;
			}
			dispatch({ type: CONSTANTS.REDEEM_DEPOSIT_FREEBET_FAILURE });
			return null;
		} catch (error) {
			dispatch({ type: CONSTANTS.REDEEM_DEPOSIT_FREEBET_FAILURE });
			return null;
		}
	};

const redeemDepositFreespin =
	(freespinId: number): ThunkAction =>
	async dispatch => {
		dispatch({ type: CONSTANTS.REDEEM_DEPOSIT_FREESPIN_START });
		try {
			const response = await wrappedRequestApi({
				endpoint: '/freespin/takeDepositFreespin',
				payload: { freespinId },
				method: 'POST',
				urlencodedContentType: true
			});

			clearDepositFreespinInSessionStorage();
			// clearCookie('selectedDepositFreespin');
			if (response && !('error' in response)) {
				return dispatch({
					type: CONSTANTS.REDEEM_DEPOSIT_FREESPIN_SUCCESS,
					payload: response
				});
			}

			return dispatch({ type: CONSTANTS.REDEEM_DEPOSIT_FREESPIN_FAILURE });
		} catch (error) {
			return dispatch({ type: CONSTANTS.REDEEM_DEPOSIT_FREESPIN_FAILURE });
		}
	};

const initialState = {
	allowedForMethod: null,
	bonusId: null,
	welcomeBonusPackageId: null,
	selectedWelcomePackageId: null,
	availableDepositOffers: [],
	availableWelcomeBonusPackages: [],
	unsplitDepositOffers: [],
	pending: true,
	loading: true,
	isBonusModalOpen: false,
	activeBonusDetails: null,
	isDepositBonusLoading: false,
	hasDepositOfferFailure: false,
	isPreSelectedBonusLoading: false,
	hasPreSelectedBonusFailure: false,
	preSelectedBonus: null,
	isFmbLoading: false,
	hasFmbFailure: false,
	fmbOverCarousel: null,
	fmbOverCarouselId: null,
	excludedGamesForIdAreLoading: false,
	hasBonusError: false,
	includedGamesForIdAreLoading: false,
	oneClickOptInMessageOpen: false,
	bonusMaxStakeMessageOpen: false,
	isRedeemingDepositFreespin: false,
	bonusMaxStakeMessageContent: '',
	isPollingBonuses: false,
	bonusesHydrated: false,
	bonusSkipped: false,
	selectedDepositFreespin: null,
	selectedDepositFreebet: null,
	selectedDepositOptIn: null,
	redeemedDepositFreespin: {},
	depositFreespinSuccess: false,
	redeemedDepositFreebet: {},
	depositFreebetSuccess: false,
	playerRemainingBonusBalance: 24,
	checkBonusFailurePayload: null,
	isRedeemingDepositFreebet: false,
	isRedeemingDepositOptIn: false
};

function reducer(
	state = initialState,
	{
		type,
		payload
	}: {
		type: string;
		payload: {
			packageId?: number;
			continuePendingNavigation?: boolean;
			paymentMethodIdsStr?: string;
			allowedForMethod?: boolean;
			result?: object;
			onId?: number;
			bool?: boolean;
			data?: object;
			category?: string;
			offers?: unknown;
			unsplitOffers?: unknown;
		};
	} = { type: '', payload: {} }
) {
	// eslint-disable-line
	switch (type) {
		case CONSTANTS.FETCH_DEPOSIT_OFFER_LOADING:
			return {
				...state,
				isDepositBonusLoading: true
			};
		case CONSTANTS.FETCH_DEPOSIT_OFFER_FAILURE:
			return {
				...state,
				isDepositBonusLoading: false,
				hasDepositOfferFailure: true
			};
		case CONSTANTS.CLEAR_DEPOSIT_FREESPIN:
			return {
				...state,
				selectedDepositFreespin: null
			};
		case CONSTANTS.CLEAR_DEPOSIT_OPT_IN:
			return {
				...state,
				selectedDepositOptIn: null
			};
		case CONSTANTS.CLEAR_DEPOSIT_FREEBET:
			return {
				...state,
				selectedDepositFreebet: null
			};
		case CONSTANTS.BONUS_ALLOWED_BY_PAYMENT:
			return {
				...state,
				...payload
			};
		case CONSTANTS.SELECT_BONUS:
			return {
				...state,
				...payload
			};
		case CONSTANTS.SELECT_BONUS_PACKAGE:
			return {
				...state,
				selectedWelcomePackageId: payload.packageId,
				continuePendingNavigation: payload.continuePendingNavigation,
				paymentMethodIdsStr: payload.paymentMethodIdsStr,
				allowedForMethod: payload.allowedForMethod
			};
		case CONSTANTS.CLEAR_PAYMENT_METHOD_ID_STR:
			return {
				...state,
				paymentMethodIdsStr: null
			};
		case CONSTANTS.CANCEL_PENDING_NAV:
			return { ...state, continuePendingNavigation: null };
		case CONSTANTS.CLEAR_BONUS:
			return {
				...state,
				bonusId: null,
				bid: null,
				bonusAddOnId: null,
				bonusAddOnOptionsId: null,
				allowedForMethod: true,
				selectedDepositFreespin: null,
				selectedDepositFreebet: null,
				selectedDepositOptIn: null,
				depositMethodId: null,
				selectedWelcomePackageId: null
			};
		case CONSTANTS.SKIP_BONUS:
			return { ...state, bonusSkipped: true };
		case CONSTANTS.FETCH_BONUS_FAILURE:
			return { ...state, loading: false, hasBonusError: true };
		case CONSTANTS.CLEAR_HAS_BONUS_ERROR:
			return { ...state, hasBonusError: false };
		case CONSTANTS.FETCH_BONUS_START:
			return { ...state, loading: true, hasBonusError: false };
		case CONSTANTS.FETCH_FREEMONEY_BONUS:
			return {
				...state,
				selectedDepositFreespin: null,
				depositFreespinSuccess: false,
				selectedDepositFreebet: null,
				selectedDepositOptIn: null,
				depositFreebetSuccess: false,
				redeemFreeMoneyBonus: {
					...payload
				}
			};
		case CONSTANTS.FETCH_BONUS_SUCCESS:
			return {
				...state,
				loading: false,
				bonusesHydrated: true,
				availableDepositOffers: payload.result,
				availableBonusesOnId: payload.onId,
				hasBonusError: false
			};
		case CONSTANTS.FETCH_WELCOME_BONUS_PACKAGE_SUCCESS:
			return {
				...state,
				loading: false,
				bonusesHydrated: true,
				availableDepositOffers: [],
				availableWelcomeBonusPackages: payload,
				hasBonusError: false
			};
		case SELECT_PAYMENT_METHOD:
			return { ...state, depositMethodId: payload };
		case LOGOUT:
			return initialState;
		case CLEAR_ALL:
			/* to be able to reset state when switching stories in storybook */
			/* Clear state, except for selectedDepositFreespin. The ID is used to make a request after deposit is done in payment-result.js */
			return {
				...initialState,
				selectedDepositFreespin: { ...(state.selectedDepositFreespin as object | null) }
			};
		case CONSTANTS.BONUS_CLEAR_ALL:
			return {
				...initialState
			};
		case CONSTANTS.TOGGLE_SHOW_BONUS_MODAL:
			return {
				...state,
				isBonusModalOpen: payload
			};
		case CONSTANTS.TOGGLE_BONUS_LOADING_STATE:
			return {
				...state,
				isDepositBonusLoading: payload.bool
			};

		case CONSTANTS.FETCH_EXCLUDED_GAMES_FOR_ID_REQUESTED:
			return {
				...state,
				excludedGamesForIdAreLoading: true
			};
		case CONSTANTS.FETCH_EXCLUDED_GAMES_FOR_ID_SUCCESS:
			return {
				...state,
				excludedGamesForIdAreLoading: false,
				excludedGamesForId: payload
			};
		case CONSTANTS.FETCH_EXCLUDED_GAMES_FOR_ID_FAUILURE:
			return {
				...state,
				excludedGamesForIdAreLoading: false
			};
		case CONSTANTS.FETCH_INCLUDED_GAMES_FOR_ID_REQUESTED:
			return {
				...state,
				includedGamesForIdAreLoading: true
			};
		case CONSTANTS.FETCH_INCLUDED_GAMES_FOR_ID_SUCCESS:
			return {
				...state,
				includedGamesForIdAreLoading: false,
				includedGamesForId: payload
			};
		case CONSTANTS.FETCH_INCLUDED_GAMES_FOR_ID_FAILURE:
			return {
				...state,
				includedGamesForIdAreLoading: false
			};
		case CONSTANTS.PLAYER_REMAINING_BONUS_BALANCE:
			return {
				...state,
				playerRemainingBonusBalance: payload
			};
		case CONSTANTS.CLEAR_INCLUDED_EXCLUDED_GAMES_FOR_ID:
			return {
				...state,
				excludedGamesForId: null,
				includedGamesForId: null,
				excludedGamesForIdAreLoading: false,
				includedGamesForIdAreLoading: false
			};
		case CONSTANTS.FETCH_DEPOSIT_OFFER_SUCCESS:
			return {
				...state,
				activeBonusDetails: payload.data,
				isDepositBonusLoading: false,
				hasDepositOfferFailure: false
			};
		case 'SET_PACKAGE_AS_ACTIVE_BONUS_DETAILS':
			return {
				...state,
				activeBonusDetails: payload
			};
		case CONSTANTS.CLEAR_ACTIVE_BONUS_DETAILS:
			return {
				...state,
				activeBonusDetails: null,
				parentGoToNext: null,
				nextStep: null,
				paginate: null
			};
		case CONSTANTS.SHOW_ONE_CLICK_OPT_IN_MESSAGE:
			return {
				...state,
				oneClickOptInMessageOpen: true
			};
		case CONSTANTS.HIDE_ONE_CLICK_OPT_IN_MESSAGE:
			return {
				...state,
				oneClickOptInMessageOpen: false
			};
		case CONSTANTS.SHOW_BONUS_MAX_STAKE_MESSAGE:
			return {
				...state,
				bonusMaxStakeMessageOpen: true,
				bonusMaxStakeMessageContent: payload
			};
		case CONSTANTS.HIDE_BONUS_MAX_STAKE_MESSAGE:
			return {
				...state,
				bonusMaxStakeMessageOpen: false,
				bonusMaxStakeMessageContent: null
			};
		case CONSTANTS.REDEEM_DEPOSIT_FREESPIN_START: {
			return {
				...state,
				isRedeemingDepositFreespin: true
			};
		}
		case CONSTANTS.REDEEM_DEPOSIT_FREESPIN_SUCCESS: {
			return {
				...state,
				isRedeemingDepositFreespin: false,
				redeemedDepositFreespin: payload,
				depositFreespinSuccess: true
			};
		}
		case CONSTANTS.REDEEM_DEPOSIT_FREESPIN_FAILURE: {
			return {
				...state,
				isRedeemingDepositFreespin: false,
				depositFreespinSuccess: false,
				redeemedDepositFreespin: {}
			};
		}
		case CONSTANTS.REDEEM_DEPOSIT_FREEBET_START: {
			return {
				...state,
				isRedeemingDepositFreebet: true
			};
		}
		case CONSTANTS.REDEEM_DEPOSIT_FREEBET_SUCCESS: {
			return {
				...state,
				isRedeemingDepositFreebet: false,
				redeemedDepositFreebet: payload,
				depositFreebetSuccess: true
			};
		}
		case CONSTANTS.REDEEM_DEPOSIT_FREEBET_FAILURE: {
			return {
				...state,
				isRedeemingDepositFreebet: false,
				depositFreebetSuccess: false,
				redeemedDepositFreebet: {}
			};
		}
		case CONSTANTS.REDEEM_DEPOSIT_OPT_IN_START: {
			return {
				...state,
				isRedeemingDepositOptIn: true
			};
		}
		case CONSTANTS.REDEEM_DEPOSIT_OPT_IN_SUCCESS: {
			return {
				...state,
				isRedeemingDepositOptIn: false,
				redeemedDepositOptIn: payload,
				depositOptInSuccess: true
			};
		}
		case CONSTANTS.REDEEM_DEPOSIT_OPT_IN_FAILURE: {
			return {
				...state,
				isRedeemingDepositOptIn: false,
				depositOptInSuccess: false,
				redeemedDepositOptIn: {}
			};
		}
		case CONSTANTS.SELECT_DEPOSIT_FREESPIN: {
			return {
				...state,
				selectedDepositFreespin: payload,
				continuePendingNavigation: true
			};
		}
		case CONSTANTS.SELECT_DEPOSIT_FREEBET: {
			return {
				...state,
				selectedDepositFreebet: payload,
				continuePendingNavigation: true
			};
		}
		case CONSTANTS.SELECT_DEPOSIT_OPT_IN: {
			return {
				...state,
				selectedDepositOptIn: payload,
				continuePendingNavigation: true
			};
		}
		case CONSTANTS.SELECT_CAMPAIGN_ACCESS: {
			return {
				...state,
				selectedCampaignAccess: payload,
				continuePendingNavigation: true
			};
		}
		case CONSTANTS.CLEAR_AVAILABLE_BONUSES: {
			return {
				...state,
				availableDepositOffers: [],
				availableWelcomeBonusPackages: [],
				unsplitDepositOffers: []
			};
		}
		case CONSTANTS.FETCH_PRESELECTED_BONUS_LOADING: {
			return {
				...state,
				hasPreSelectedBonusFailure: false,
				isPreSelectedBonusLoading: true,
				preSelectedBonus: null
			};
		}
		case CONSTANTS.FETCH_PRESELECTED_BONUS_SUCCESS: {
			return {
				...state,
				hasPreSelectedBonusFailure: false,
				isPreSelectedBonusLoading: false,
				preSelectedBonus: payload
			};
		}
		case CONSTANTS.FETCH_PRESELECTED_BONUS_FAILURE: {
			return {
				...state,
				hasPreSelectedBonusFailure: false,
				isPreSelectedBonusLoading: false,
				preSelectedBonus: null
			};
		}
		case CONSTANTS.FETCH_FMB_START: {
			return {
				...state,
				hasFmbFailure: false,
				isFmbLoading: true
			};
		}
		case CONSTANTS.FETCH_FMB_SUCCESS: {
			return {
				...state,
				isFmbLoading: false,
				hasFmbFailure: false,
				fmbOverCarousel: payload
			};
		}
		case CONSTANTS.FETCH_FMB_FAILURE: {
			return {
				...state,
				fmbOverCarousel: null,
				hasFmbFailure: true,
				isFmbLoading: false
			};
		}
		case CONSTANTS.REDEEM_FMB_OVER_CAROUSEL_FAIL: {
			return {
				...state
			};
		}
		case CONSTANTS.REDEEM_FMB_OVER_CAROUSEL_SUCCESS: {
			return {
				...state,
				fmbOverCarousel: null,
				hasFmbFailure: false,
				isFmbLoading: false
			};
		}
		case CONSTANTS.CHECK_BONUS_FAILURE: {
			return {
				...state,
				checkBonusFailurePayload: payload
			};
		}
		case CONSTANTS.SET_BONUS_SPLIT_CATEGORY: {
			return {
				...state,
				bonusSplitCategory: payload.category,
				availableDepositOffers: payload.offers,
				unsplitDepositOffers: payload.unsplitOffers
			};
		}
		default:
			return state;
	}
}

reducerRegistry.register(REDUCER_NAME, reducer);

export {
	allowsMethod,
	cancelPendingNavigation,
	clearActiveBonusDetails,
	clearAvailableBonuses,
	clearBonus,
	clearBonusAll,
	clearDepositFreebet,
	clearDepositFreespin,
	clearDepositOptIn,
	clearHasBonusError,
	closeVerification,
	confirmFreeMoneyBonusResult,
	fetchBonusDepositItem,
	fetchFmbOverCarousel,
	fetchPreSelectedBonus,
	formatBonusAmount,
	getBonusPackageIdInSessionStorage,
	getDepositFreebetInSessionStorage,
	getDepositFreebetRedirectUrlInSessionStorage,
	getDepositFreespinInSessionStorage,
	getDepositOffers,
	getDepositOptInInSessionStorage,
	getNoAccountWelcomeBonuses,
	getWelcomeBonusPackages,
	initialState,
	isAllowedForMethod,
	isDepositBonusOk,
	logPlayerRewardAction,
	redeemDepositFreebet,
	redeemDepositFreespin,
	redeemDepositOptIn,
	redeemFreeMoneyBonusOverCarousel,
	setBonusPackageToActiveBonusDetail,
	skipBonus,
	toggleBonusDetailModal,
	toggleBonusLoadingState
};
export default reducer;
